import { Bottom, Button } from '@dragonpass/dragon-design-mobile'
import { Form, Input, Popup } from 'antd-mobile'
import { CloseOutline } from 'antd-mobile-icons'
import styles from './style.scss'
import { PaymentConfig } from '@/interface/pay'
import { useEffect } from 'react'
import useLang from '@/libs/hooks/useLang'
import { TrackView } from '@dragonpass/intl-unified-tracker'
import DpTracker from '@/libs/components/DpTrackerComponents/DpTracker'

export interface ICardBinInfo {
  cardNumber: string
  cardHolderName?: string
  expireTime?: string
  cvv?: string
}

interface Iprops {
  visible: boolean
  onOk(cardInfo: ICardBinInfo): void
  onCancel(): void
  payAmount: number
  payTypeLogo: string
  payTypeName: string
  cardBinInfo: PaymentConfig
  code: string
  currencySymbol?: string
}

export default function PaymentModal(props: Iprops) {
  const {
    visible,
    onOk,
    onCancel,
    payAmount,
    payTypeLogo,
    payTypeName = '',
    cardBinInfo = {},
    code,
    currencySymbol,
  } = props

  const { t } = useLang('pay_util')
  const [form] = Form.useForm()
  const isIntl = code === 'overseas'

  const confirmToPay = async () => {
    const values = await form.validateFields()
    console.log('values:', values)
    onOk(values)
  }

  const reset = () => {
    form.setFieldsValue({
      cardNumber: '',
      cardHolderName: '',
      expireTime: '',
      cvv: '',
    })
    onCancel()
  }

  useEffect(() => {
    form.setFieldsValue({
      cardNumber: '',
      cardHolderName: '',
      expireTime: '',
      cvv: '',
    })
  }, [])

  return (
    <Popup
      visible={visible}
      onMaskClick={reset}
      destroyOnClose
      bodyStyle={{
        width: '100vw',
        background: 'transparent',
      }}
      onClose={reset}
    >
      <DpTracker>
        <TrackView
          trackClk={false}
          trackData={{
            module_name: '卡号弹出框',
          }}
          trackExp={true}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <div className={styles.paymentModal}>
            <div className={styles.payPrice}>
              <span className={styles.payUnit}>{currencySymbol || '¥'}</span>
              {payAmount}
            </div>
            <CloseOutline onClick={onCancel} className={styles.closeBtn} />
            <div className={styles.content}>
              <div className={styles.payInfo}>
                <img
                  src={payTypeLogo}
                  alt={payTypeName}
                  className={styles.payTypeLogo}
                />
                <span className={styles.payTypeName}>{payTypeName}</span>
              </div>
              <Form layout="horizontal" form={form} className={styles.cardForm}>
                <div className={styles.cardInfo}>
                  <img
                    src={cardBinInfo.logo}
                    alt=""
                    className={styles.cardLogo}
                  />
                  <span className={styles.cardName}>{cardBinInfo.title}</span>
                  <span className={styles.remarks}>{cardBinInfo.remarks}</span>
                </div>
                <Form.Item
                  className={styles.formItem}
                  name="cardNumber"
                  label={t('cardNo', '卡号')}
                  style={{
                    '--border-inner': 'none',
                  }}
                  rules={[
                    {
                      required: true,
                      message: t('enterCardNo', '请输入卡号'),
                    },
                  ]}
                >
                  <Input
                    className={styles.cardInput}
                    placeholder={t('enterCardNo', '请输入卡号')}
                  />
                </Form.Item>

                <Form.Item
                  className={styles.formItem}
                  name="cardHolderName"
                  label={t('name', '姓名')}
                  hidden={!isIntl}
                  rules={[
                    {
                      required: isIntl,
                      message: t('enterCardHolderUsername', '请输入持卡人姓名'),
                    },
                  ]}
                >
                  <Input
                    className={styles.cardInput}
                    placeholder={t(
                      'enterCardHolderUsername',
                      '请输入持卡人姓名',
                    )}
                  />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  name="expireTime"
                  label={t('exprieDate', '有效期')}
                  hidden={!isIntl}
                  rules={[
                    {
                      max: 4,
                    },
                    {
                      required: isIntl,
                      message: t('exprieDatePlaceholder', '请输入有效期'),
                    },
                    isIntl
                      ? {
                          pattern: /^(0[1-9]|1[0-2])\d{2}$/,
                          message: t(
                            'exprieDateRuleMessage',
                            '请输入正确的过期时间',
                          ),
                        }
                      : {},
                  ]}
                >
                  <Input
                    maxLength={4}
                    className={styles.cardInput}
                    placeholder={t(
                      'bankAndCreditCardPlaceholder',
                      '请输入有效期（XXXX）',
                    )}
                  />
                </Form.Item>
                <Form.Item
                  className={styles.formItem}
                  name="cvv"
                  hidden={!isIntl}
                  label={t('cvvCode', 'CVV码')}
                  rules={[
                    {
                      required: isIntl,
                      message: t('cvvCodePlaceholder', '请输入cvv码'),
                    },
                  ]}
                >
                  <Input
                    className={styles.cardInput}
                    placeholder={t(
                      'enter3or4DigitNumPlaceholder',
                      '请输入银行卡/信用卡背面的3/4位数字',
                    )}
                  />
                </Form.Item>
              </Form>
            </div>
            <Bottom
              style={{
                '--dpm-color-background': 'var(--secondary-background-color1)',
              }}
              onClick={confirmToPay}
              fixed={false}
            >
              <Button
                style={{
                  '--dpm-button-border-radius': '24px',
                  '--border-color': 'none',
                  '--background-color': 'var(--button-background-color)',
                  '--border-radius': 'var(--button-radius)',
                  '--text-color': 'var(--button-text-color)',
                  background:
                    'var(--background-color, var(--dpm-color-primary))',
                }}
                block
                color="primary"
              >
                {t('confirmToPay', '确认支付')}
              </Button>
            </Bottom>
          </div>
        </TrackView>
      </DpTracker>
    </Popup>
  )
}
